import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import '../styles/CambiaPassword.css';

const LUNGHEZZA_MINIMA = 8;

const IcoOcchio = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const IcoOcchioBarrato = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.6 6.1A9.9 9.9 0 0 1 12 5.5c6.4 0 10 6.5 10 6.5a17.6 17.6 0 0 1-3.3 4.1M6.4 7.7A17.4 17.4 0 0 0 2 12s3.6 6.5 10 6.5a9.7 9.7 0 0 0 4-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <path d="M3 3l18 18" />
    </svg>
);

const CampoPassword = ({ id, label, valore, onChange, placeholder, disabled }) => {
    const [visibile, setVisibile] = useState(false);
    return (
        <div className="cp-group">
            <label htmlFor={id}>{label}</label>
            <div className="cp-input-wrap">
                <input
                    type={visibile ? 'text' : 'password'}
                    id={id}
                    value={valore}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    autoComplete="new-password"
                    required
                    disabled={disabled}
                />
                <button
                    type="button"
                    className="cp-occhio"
                    onClick={() => setVisibile((v) => !v)}
                    disabled={disabled}
                    aria-label={visibile ? 'Nascondi la password' : 'Mostra la password'}
                    title={visibile ? 'Nascondi la password' : 'Mostra la password'}
                >
                    {visibile ? <IcoOcchioBarrato /> : <IcoOcchio />}
                </button>
            </div>
        </div>
    );
};

/**
 * ReimpostaPassword
 * Raggiunta dal collegamento ricevuto via email.
 *
 * L'apertura della pagina non consuma il token: la verifica avviene
 * solo all'invio. Cosi' un antivirus che visita il collegamento per
 * controllarlo non lo brucia prima che lo apra l'utente.
 */
const ReimpostaPassword = () => {
    const [parametri] = useSearchParams();
    const token = parametri.get('t') || '';

    const [nuova, setNuova] = useState('');
    const [conferma, setConferma] = useState('');
    const [errore, setErrore] = useState('');
    const [loading, setLoading] = useState(false);
    const [fatto, setFatto] = useState(false);
    const [tokenScaduto, setTokenScaduto] = useState(false);

    const navigate = useNavigate();

    const troppoCorta = nuova.length > 0 && nuova.length < LUNGHEZZA_MINIMA;
    const coincidono = conferma.length > 0 && nuova === conferma;
    const divergono = conferma.length > 0 && nuova !== conferma;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrore('');

        if (nuova.length < LUNGHEZZA_MINIMA) {
            setErrore(`La password deve avere almeno ${LUNGHEZZA_MINIMA} caratteri.`);
            return;
        }
        if (nuova !== conferma) {
            setErrore('Le due password non coincidono.');
            return;
        }

        setLoading(true);
        try {
            const risposta = await authAPI.reimpostaPassword({ token, password_nuova: nuova });
            if (risposta.data.success) setFatto(true);
        } catch (err) {
            if (err.code === 'TOKEN_NON_VALIDO') {
                setTokenScaduto(true);
            } else {
                setErrore(err.message || 'Non è stato possibile aggiornare la password.');
            }
        } finally {
            setLoading(false);
        }
    };

    if (!token || tokenScaduto) {
        return (
            <div className="cp-container">
                <div className="cp-card">
                    <div className="cp-header">
                        <div className="cp-logo">R</div>
                        <h1 className="cp-titolo">Collegamento non più valido</h1>
                        <p className="cp-sottotitolo">
                            I collegamenti di recupero valgono 30 minuti e possono essere
                            usati una sola volta. Richiedine uno nuovo.
                        </p>
                    </div>
                    <button type="button" className="cp-btn" onClick={() => navigate('/password-dimenticata')}>
                        Richiedi un nuovo collegamento
                    </button>
                    <div className="cp-piede">
                        <span />
                        <button type="button" className="cp-esci" onClick={() => navigate('/login')}>
                            Torna all'accesso
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (fatto) {
        return (
            <div className="cp-container">
                <div className="cp-card">
                    <div className="cp-header">
                        <div className="cp-logo">R</div>
                        <h1 className="cp-titolo">Password aggiornata</h1>
                        <p className="cp-sottotitolo">
                            Ora puoi accedere con la password che hai appena scelto.
                        </p>
                    </div>
                    <button type="button" className="cp-btn" onClick={() => navigate('/login')}>
                        Vai all'accesso
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="cp-container">
            <div className="cp-card">
                <div className="cp-header">
                    <div className="cp-logo">R</div>
                    <h1 className="cp-titolo">Scegli una nuova password</h1>
                    <p className="cp-sottotitolo">
                        Dopo il salvataggio potrai accedere subito con questa password.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="cp-form">
                    {errore && <div className="cp-errore">{errore}</div>}

                    <div className="cp-blocco">
                        <CampoPassword
                            id="nuova"
                            label="Nuova password"
                            valore={nuova}
                            onChange={setNuova}
                            placeholder={`Almeno ${LUNGHEZZA_MINIMA} caratteri`}
                            disabled={loading}
                        />
                        {troppoCorta && (
                            <div className="cp-riscontro cp-riscontro-ko">
                                Mancano {LUNGHEZZA_MINIMA - nuova.length} caratteri
                            </div>
                        )}
                    </div>

                    <div className="cp-blocco">
                        <CampoPassword
                            id="conferma"
                            label="Ripeti la nuova password"
                            valore={conferma}
                            onChange={setConferma}
                            placeholder="Riscrivila per conferma"
                            disabled={loading}
                        />
                        {coincidono && (
                            <div className="cp-riscontro cp-riscontro-ok">✓ Le password coincidono</div>
                        )}
                        {divergono && (
                            <div className="cp-riscontro cp-riscontro-ko">Le password non coincidono</div>
                        )}
                    </div>

                    <button type="submit" className="cp-btn" disabled={loading}>
                        {loading ? 'Salvataggio…' : 'Salva la password'}
                    </button>
                </form>

                <div className="cp-piede">
                    <span />
                    <button type="button" className="cp-esci" onClick={() => navigate('/login')} disabled={loading}>
                        Torna all'accesso
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReimpostaPassword;
